// src/pages/supervisor/SupervisorNotifications.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Grid,
    Card,
    CardContent,
    Stack,
    Tabs,
    Tab,
    Badge,
    Tooltip,
    Alert,
    Snackbar,
    CircularProgress,
    InputAdornment,
    Divider,
    Container
} from '@mui/material';
import {
    Delete,
    Visibility,
    CheckCircle,
    Warning,
    Error,
    Info,
    DoneAll,
    Refresh,
    Search,
    Clear,
    NotificationsNone,
    NotificationsActive,
    Email,
    Person,
    Schedule,
    Category,
    School as SchoolIcon,
    OpenInNew
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import SupervisorBar from '../../components/SupervisorBar';
import { formatDateTime, formatTableDate, getUserDetails } from '../../utils/helpers';
import { 
    APISupervisorNotifications,
    APISupervisorNotificationsStatistics,
    APISupervisorNotificationsMarkRead,
    APISupervisorNotificationsMarkAllRead,
    APISupervisorNotificationsDelete
} from '../../apiServices/APIs';
import { notificationService } from '../../apiServices/notificationService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const SupervisorNotifications = () => {
    const theme = useTheme();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [filterTab, setFilterTab] = useState(0);
    const [filterType, setFilterType] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [supervisorId, setSupervisorId] = useState(null);

    const tabs = [
        { label: 'All', value: 'all', icon: <NotificationsNone /> },
        { label: 'Unread', value: 'unread', icon: <NotificationsActive />, badge: true },
        { label: 'Read', value: 'read', icon: <CheckCircle /> }
    ];

    const notificationTypes = [
        { value: '', label: 'All Types' },
        { value: 'info', label: 'Info', icon: <Info /> },
        { value: 'success', label: 'Success', icon: <CheckCircle /> },
        { value: 'warning', label: 'Warning', icon: <Warning /> },
        { value: 'error', label: 'Error', icon: <Error /> }
    ];

    // Get supervisor ID from user details
    useEffect(() => {
        const id = getUserDetails("Id");
        setSupervisorId(id);
    }, []);

    // Fetch notifications using notificationService
    const fetchNotifications = useCallback(async () => {
        if (!supervisorId) return;
        
        try {
            setLoading(true);
            const params = {
                supervisor_id: supervisorId,
                page: page + 1,
                per_page: rowsPerPage,
                ...(filterTab === 1 && { is_read: false }),
                ...(filterTab === 2 && { is_read: true }),
                ...(filterType && { type: filterType }),
                ...(searchTerm && { search: searchTerm })
            };
            
            const response = await notificationService.getAll(APISupervisorNotifications, params);
            
            if (response.success) {
                setNotifications(response.data || []);
                setTotalCount(response.pagination?.total || 0);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
            setSnackbar({ open: true, message: 'Failed to load notifications', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [supervisorId, page, rowsPerPage, filterTab, filterType, searchTerm]);

    // Fetch statistics using notificationService
    const fetchStatistics = async () => {
        if (!supervisorId) return;
        
        try {
            setStatsLoading(true);
            const response = await notificationService.getStatistics(APISupervisorNotificationsStatistics, {
                supervisor_id: supervisorId
            });
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Error fetching statistics:', error);
        } finally {
            setStatsLoading(false);
        }
    };

    useEffect(() => {
        if (supervisorId) {
            fetchNotifications();
            fetchStatistics();
        }
    }, [supervisorId, fetchNotifications]);

    // Mark as read using notificationService
    const handleMarkAsRead = async (id) => {
        try {
            const response = await notificationService.markAsRead(
                APISupervisorNotificationsMarkRead(id),
                {}
            );
            if (response.success) {
                setSnackbar({ open: true, message: 'Notification marked as read', severity: 'success' });
                fetchNotifications();
                fetchStatistics();
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to mark as read', severity: 'error' });
        }
    };

    // Mark all as read using notificationService
    const handleMarkAllAsRead = async () => {
        try {
            const response = await notificationService.markAllAsRead(
                APISupervisorNotificationsMarkAllRead,
                { supervisor_id: supervisorId }
            );
            if (response.success) {
                setSnackbar({ open: true, message: 'All notifications marked as read', severity: 'success' });
                fetchNotifications();
                fetchStatistics();
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to mark all as read', severity: 'error' });
        }
    };

    // Delete notification using notificationService
    const handleDelete = async () => {
        if (!selectedNotification) return;
        
        try {
            const response = await notificationService.delete(
                APISupervisorNotificationsDelete(selectedNotification.id)
            );
            if (response.success) {
                setSnackbar({ open: true, message: 'Notification deleted successfully', severity: 'success' });
                setDeleteDialogOpen(false);
                setSelectedNotification(null);
                fetchNotifications();
                fetchStatistics();
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to delete notification', severity: 'error' });
        }
    };

    // Handle action URL click
    const handleActionUrlClick = (url) => {
        if (url) {
            window.open(url, '_blank');
        }
    };

    // Get notification icon
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'success': return <CheckCircle sx={{ color: '#4caf50' }} />;
            case 'warning': return <Warning sx={{ color: '#ff9800' }} />;
            case 'error': return <Error sx={{ color: '#f44336' }} />;
            default: return <Info sx={{ color: '#2196f3' }} />;
        }
    };

    // Get status chip
    const getStatusChip = (isRead) => {
        return (
            <Chip
                label={isRead ? 'Read' : 'Unread'}
                size="small"
                sx={{
                    bgcolor: isRead ? '#e8f5e9' : '#ffebee',
                    color: isRead ? '#2e7d32' : '#c62828',
                    fontWeight: 600
                }}
            />
        );
    };

    // Clear filters
    const handleClearFilters = () => {
        setFilterTab(0);
        setFilterType('');
        setSearchTerm('');
        setPage(0);
    };

    return (
        <FullWidthContainer>
            <SupervisorBar />
            <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1 }}>
                        My Notifications
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        View and manage your Research Supervisor notifications
                    </Typography>
                </Box>

                {/* Statistics Cards */}
                {stats && (
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Total</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.total}</Typography>
                                        </Box>
                                        <NotificationsNone sx={{ fontSize: 40, color: theme.palette.primary.main, opacity: 0.7 }} />
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Unread</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#f44336' }}>{stats.unread}</Typography>
                                        </Box>
                                        <NotificationsActive sx={{ fontSize: 40, color: '#f44336', opacity: 0.7 }} />
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Last 7 Days</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.last_7_days}</Typography>
                                        </Box>
                                        <Schedule sx={{ fontSize: 40, color: '#ff9800', opacity: 0.7 }} />
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">By Type</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                {Object.keys(stats.by_type || {}).length}
                                            </Typography>
                                        </Box>
                                        <Category sx={{ fontSize: 40, color: '#9c27b0', opacity: 0.7 }} />
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Filters and Actions */}
                <Paper sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                        <Tabs value={filterTab} onChange={(e, v) => setFilterTab(v)}>
                            {tabs.map((tab, idx) => (
                                <Tab
                                    key={idx}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {tab.icon}
                                            {tab.label}
                                            {tab.badge && stats?.unread > 0 && (
                                                <Badge badgeContent={stats.unread} color="error" sx={{ ml: 1 }} />
                                            )}
                                        </Box>
                                    }
                                />
                            ))}
                        </Tabs>
                    </Box>

                    <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
                            <TextField
                                size="small"
                                placeholder="Search notifications..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{ minWidth: 250 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchTerm('')}>
                                                <Clear />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value)}
                                    label="Type"
                                >
                                    {notificationTypes.map(type => (
                                        <MenuItem key={type.value} value={type.value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {type.icon}
                                                {type.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {(filterTab !== 0 || filterType || searchTerm) && (
                                <Button
                                    size="small"
                                    onClick={handleClearFilters}
                                    startIcon={<Clear />}
                                    variant="outlined"
                                >
                                    Clear Filters
                                </Button>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Refresh">
                                <IconButton onClick={() => fetchNotifications()}>
                                    <Refresh />
                                </IconButton>
                            </Tooltip>
                            {stats?.unread > 0 && (
                                <Button
                                    variant="contained"
                                    startIcon={<DoneAll />}
                                    onClick={handleMarkAllAsRead}
                                    size="small"
                                >
                                    Mark All Read
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Paper>

                {/* Notifications Table */}
                <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: theme.palette.grey[50] }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Institution</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : notifications.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            No notifications found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                notifications.map((notification) => (
                                    <TableRow key={notification.id} sx={{ bgcolor: !notification.is_read ? theme.palette.action.hover : 'inherit' }}>
                                        <TableCell>{getNotificationIcon(notification.type)}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: !notification.is_read ? 700 : 400 }}>
                                                {notification.title}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                                {notification.message.length > 60 
                                                    ? `${notification.message.substring(0, 60)}...` 
                                                    : notification.message}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <SchoolIcon fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    {notification.university_name || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{getStatusChip(notification.is_read)}</TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatTableDate(notification.created_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedNotification(notification);
                                                            setDetailsDialogOpen(true);
                                                            if (!notification.is_read) {
                                                                handleMarkAsRead(notification.id);
                                                            }
                                                        }}
                                                    >
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {!notification.is_read && (
                                                    <Tooltip title="Mark as Read">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleMarkAsRead(notification.id)}
                                                            color="primary"
                                                        >
                                                            <CheckCircle fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedNotification(notification);
                                                            setDeleteDialogOpen(true);
                                                        }}
                                                        color="error"
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </TableContainer>

                {/* Notification Details Dialog */}
                <Dialog
                    open={detailsDialogOpen}
                    onClose={() => setDetailsDialogOpen(false)}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 3 } }}
                >
                    {selectedNotification && (
                        <>
                            <DialogTitle sx={{ 
                                bgcolor: theme.palette.primary.main, 
                                color: 'white',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {getNotificationIcon(selectedNotification.type)}
                                    <Typography variant="h6">{selectedNotification.title}</Typography>
                                </Box>
                                <IconButton onClick={() => setDetailsDialogOpen(false)} sx={{ color: 'white' }}>
                                    <Clear />
                                </IconButton>
                            </DialogTitle>
                            <DialogContent sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.grey[50] }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <SchoolIcon fontSize="small" color="action" />
                                                    <Typography variant="caption" color="text.secondary">Institution:</Typography>
                                                    <Typography variant="body2">
                                                        {selectedNotification.university_name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Schedule fontSize="small" color="action" />
                                                    <Typography variant="caption" color="text.secondary">Created:</Typography>
                                                    <Typography variant="body2">
                                                        {formatDateTime(selectedNotification.created_at)}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            {selectedNotification.read_at && (
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <CheckCircle fontSize="small" color="action" />
                                                        <Typography variant="caption" color="text.secondary">Read at:</Typography>
                                                        <Typography variant="body2">
                                                            {formatDateTime(selectedNotification.read_at)}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Paper>

                                    <Divider />

                                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                                        {selectedNotification.message}
                                    </Typography>

                                    {selectedNotification.action_url && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<OpenInNew />}
                                            onClick={() => handleActionUrlClick(selectedNotification.action_url)}
                                            fullWidth
                                        >
                                            View Related Item
                                        </Button>
                                    )}

                                    {selectedNotification.metadata && (
                                        <Paper variant="outlined" sx={{ p: 2 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                                Additional Information
                                            </Typography>
                                            <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace', overflow: 'auto' }}>
                                                {JSON.stringify(selectedNotification.metadata, null, 2)}
                                            </pre>
                                        </Paper>
                                    )}
                                </Stack>
                            </DialogContent>
                            <DialogActions sx={{ p: 2.5 }}>
                                <Button onClick={() => setDetailsDialogOpen(false)} variant="contained">
                                    Close
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Delete Notification</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete this notification? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleDelete} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </FullWidthContainer>
    );
};

export default SupervisorNotifications;